import React, {
    RefObject,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Scrollbars } from "react-custom-scrollbars-2";
import { Loader } from "@design-system/Loader/Loader";
import "./IntegrationsSettings.scss";
import { useIntegrationConnections } from "@api/integrationConnection.api";
import { clearConnectionError } from "@helpers/connectionError";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { useUserIntegrations } from "@hooks/useUserIntegrations";
import { useStripeExpressReturn } from "@hooks/useStripeExpressReturn";
import { useSettings } from "../SettingsProvider";
import { AddAccount } from "../../accounts/AddAccount";
import { TablePageHeader } from "../../pageHeader/TablePageHeader";
import { FinancialConnectionsSettings } from "./FinancialConnectionsSettings";
import {
    filterFinancialConnections,
    filterIntegrationConnections,
} from "./integrationsSearch";

interface Props {
    scrollbars: RefObject<Scrollbars | null>;
}

export const IntegrationsSettings: React.FC<Props> = ({ scrollbars }) => {
    const [search, setSearch] = useState("");
    useStripeExpressReturn();
    const { path } = useSettings();
    const isDemoWorkspace = useActiveWorkspace()?.isDemo ?? false;
    const actionRef = useRef<string | null>(null);

    useEffect(() => {
        const [, action] = path;
        actionRef.current = action;
    }, [path]);

    const integrations = useUserIntegrations({
        select: (data) =>
            isDemoWorkspace
                ? {
                      ...data,
                      financialConnections: data.financialConnections.map(
                          (financialConnection) => ({
                              ...financialConnection,
                              connection: clearConnectionError(
                                  financialConnection.connection,
                              ),
                          }),
                      ),
                  }
                : data,
    });

    const integrationConnections = useIntegrationConnections();

    const handleFixAction = useCallback(() => {
        const firstBrokenConnection =
            integrations.data?.financialConnections.find(
                (fc) => !!fc.connection.error,
            );

        if (!firstBrokenConnection) {
            return;
        }

        const el = document.querySelector(
            `[data-connection="${firstBrokenConnection.connection.id}"]`,
        );

        if (el && scrollbars.current) {
            const elementRect = el.getBoundingClientRect();
            const scrollbarsRect =
                scrollbars.current.container.getBoundingClientRect();

            scrollbars.current.scrollTop(
                elementRect.top - scrollbarsRect.top - 20,
            );
        }
    }, [integrations.data, scrollbars]);

    useEffect(() => {
        if (integrations.isLoading || !actionRef.current) {
            return;
        }

        if (actionRef.current === "fix") {
            handleFixAction();
        }

        actionRef.current = null;
    }, [integrations.data, handleFixAction, integrations.isLoading]);

    const filteredIntegrations = useMemo(() => {
        if (!search) {
            return integrations.data?.financialConnections;
        }
        return (
            integrations.data &&
            filterFinancialConnections(
                integrations.data.financialConnections,
                search,
            )
        );
    }, [integrations.data, search]);

    const filteredIntegrationConnections = useMemo(() => {
        if (!search) {
            return integrationConnections.data;
        }
        return (
            integrationConnections.data &&
            filterIntegrationConnections(integrationConnections.data, search)
        );
    }, [integrationConnections.data, search]);

    if (!integrations.isSuccess || !integrationConnections.isSuccess) {
        return <Loader />;
    }

    return (
        <div className="settings__integrations">
            <header className="settings__header">
                <h3>Integrations</h3>
            </header>

            <TablePageHeader search={search} onSearchChange={setSearch}>
                <AddAccount
                    btnSize="md"
                    btnText="Connect new account"
                    btnVariant="primary"
                />
            </TablePageHeader>

            <FinancialConnectionsSettings
                connections={filteredIntegrations ?? []}
                integrationConnections={filteredIntegrationConnections ?? []}
                unfilteredConnectionsLength={
                    integrations.data?.financialConnections.length ?? 0
                }
            />
        </div>
    );
};
